import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.addEventListener('click', this.#handleMediaClick, { signal });
    this.#initThumbnailStackLayout();
    this.#initThumbnailStackScroll();
  }

  #controller = new AbortController();

  #thumbnailStackResizeObserver = new ResizeObserver(() => {
    this.#syncThumbnailStackHeight();
  });

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#thumbnailStackResizeObserver.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
    this.#setSelectedThumbnail(event.detail.index);
  };

  /**
 * Handles slideshow selection and keeps custom thumbnail stack selected state in sync.
 *
 * @param {SlideshowSelectEvent} event
 */
  #handleSlideshowSelect = (event) => {
    this.#setSelectedThumbnail(event.detail.index);
  };

  /**
 * Fallback for opening zoom dialog by clicking the main product image.
 * Needed for custom thumbnail_stack layout where declarative on:click can be lost
 * after custom slideshow/thumbnail changes.
 *
 * @param {PointerEvent} event
 */
  #handleMediaClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const buttons = this.refs.thumbnailStackButtons || [];

    let thumbnailButton = target.closest('.media-gallery__thumbnail-stack-button');

    if (!thumbnailButton) {
      thumbnailButton = buttons.find((button) => {
        const rect = button.getBoundingClientRect();

        return (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        );
      });
    }

    if (thumbnailButton && this.contains(thumbnailButton)) {
      event.preventDefault();
      event.stopPropagation();

      const index = buttons.indexOf(thumbnailButton);

      if (index < 0) return;

      this.selectMedia(index, event);
      return;
    }

    if (event.defaultPrevented) return;

    if (
      target.closest(
        'slideshow-arrows, slideshow-controls, button, a, input, select, textarea'
      )
    ) {
      return;
    }

    const slide = target.closest('slideshow-slide.product-media-container--zoomable');
    if (!slide || !this.contains(slide)) return;

    const slides = this.slideshow?.slides || [];
    const index = slides.indexOf(slide);

    if (index < 0) return;

    this.refs.zoomDialogComponent?.open(index, event);
  };

  /**
   * Selects media from the custom thumbnail stack.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  selectMedia(index, event) {
    event?.preventDefault();

    const slideshow = this.slideshow;
    const slide = slideshow?.slides?.[index];
    const scroller = slideshow?.refs?.scroller;

    if (!slideshow || !slide || !scroller) return;

    if (this.presentation === 'thumbnail_stack') {
      const previousIndex = slideshow.current ?? 0;

      scroller.scrollTo({
        left: slide.offsetLeft,
        behavior: 'instant',
      });

      slideshow.current = index;

      slideshow.slides?.forEach((item, itemIndex) => {
        item.setAttribute('aria-hidden', itemIndex === index ? 'false' : 'true');
      });

      this.#setSelectedThumbnail(index);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: event != null,
          trigger: 'select',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );

      requestAnimationFrame(() => {
        this.#syncThumbnailStackHeight();
      });

      return;
    }

    slideshow.select(index, event, { animate: false });
    this.#setSelectedThumbnail(index);

    requestAnimationFrame(() => {
      this.#syncThumbnailStackHeight();
    });
  }

  /**
   * Updates current state for custom thumbnail stack buttons.
   *
   * @param {number} index
   */
  #setSelectedThumbnail(index) {
    this.refs.thumbnailStackButtons?.forEach((button, buttonIndex) => {
      button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
    });
  }

  /**
 * Initializes desktop thumbnail stack sizing.
 */
  #initThumbnailStackLayout() {
    if (this.presentation !== 'thumbnail_stack') return;

    const main = this.querySelector('.media-gallery__thumbnail-stack-main');
    if (!main) return;

    this.#thumbnailStackResizeObserver.observe(main);

    main.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;

      image.addEventListener('load', this.#syncThumbnailStackHeight, {
        once: true,
        signal: this.#controller.signal,
      });
    });

    window.addEventListener('resize', this.#syncThumbnailStackHeight, {
      signal: this.#controller.signal,
    });

    requestAnimationFrame(() => {
      this.#syncThumbnailStackHeight();

      requestAnimationFrame(() => {
        this.#syncThumbnailStackHeight();
      });
    });
  }

  /**
  * Forces wheel/drag events to scroll the thumbnail stack instead of the page/Lenis.
  */
  #initThumbnailStackScroll() {
    if (this.presentation !== 'thumbnail_stack') return;

    const scroller = this.querySelector('.media-gallery__thumbnail-stack-list-wrapper');
    if (!scroller) return;

    let isDragging = false;
    let startY = 0;
    let startScrollTop = 0;
    let hasMoved = false;

    const isDesktop = () => !window.matchMedia('(max-width: 749px)').matches;
    const canScroll = () => scroller.scrollHeight > scroller.clientHeight;

    scroller.addEventListener(
      'wheel',
      (event) => {
        if (!isDesktop() || !canScroll()) return;

        event.preventDefault();
        event.stopPropagation();

        scroller.scrollTop += event.deltaY;
      },
      {
        passive: false,
        signal: this.#controller.signal,
      }
    );

    scroller.addEventListener(
      'pointerdown',
      (event) => {
        if (!isDesktop() || !canScroll()) return;

        isDragging = true;
        hasMoved = false;
        startY = event.clientY;
        startScrollTop = scroller.scrollTop;

        scroller.classList.add('is-dragging');
        scroller.setPointerCapture?.(event.pointerId);
      },
      {
        signal: this.#controller.signal,
      }
    );

    scroller.addEventListener(
      'pointermove',
      (event) => {
        if (!isDragging) return;

        const deltaY = event.clientY - startY;

        if (Math.abs(deltaY) > 3) {
          hasMoved = true;
        }

        event.preventDefault();
        event.stopPropagation();

        scroller.scrollTop = startScrollTop - deltaY;
      },
      {
        passive: false,
        signal: this.#controller.signal,
      }
    );

    const stopDragging = (event) => {
      if (!isDragging) return;

      isDragging = false;
      scroller.classList.remove('is-dragging');
      scroller.releasePointerCapture?.(event.pointerId);
    };

    scroller.addEventListener('pointerup', stopDragging, {
      signal: this.#controller.signal,
    });

    scroller.addEventListener('pointercancel', stopDragging, {
      signal: this.#controller.signal,
    });

    scroller.addEventListener(
      'click',
      (event) => {
        if (!hasMoved) return;

        event.preventDefault();
        event.stopPropagation();

        hasMoved = false;
      },
      {
        capture: true,
        signal: this.#controller.signal,
      }
    );
  }

  /**
   * Keeps the left thumbnails column equal to the main media height on desktop.
   */
  #syncThumbnailStackHeight = () => {
    if (this.presentation !== 'thumbnail_stack') return;

    const listWrapper = this.querySelector('.media-gallery__thumbnail-stack-list-wrapper');
    const main = this.querySelector('.media-gallery__thumbnail-stack-main');

    if (!listWrapper || !main) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      this.style.removeProperty('--thumbnail-stack-height');
      return;
    }

    const mainHeight = main.getBoundingClientRect().height;

    if (mainHeight > 0) {
      this.style.setProperty('--thumbnail-stack-height', `${mainHeight}px`);
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
